// HexCritter — ambient wildlife that peeks out of empty hexes when the
// player has been idle. Specced in docs/future/w15-w20-plan.md §2.
//
// Behavior (per spec):
//   - After 45 seconds of zero user input on a non-active screen state,
//     a small critter peeks out of one randomly-chosen empty cell.
//   - Just the top of its head — two ink-stroke eyes, ear-tips or
//     equivalent species mark, no body.
//   - Looks left, looks right, looks back at the camera, with a couple
//     of slow blinks. ~6 seconds of looking.
//   - Retreats back into the cell. Soft, polite — no scared dash.
//   - If the player still doesn't move, repeats every 30-45 seconds
//     from a different cell.
//   - The moment the player taps/clicks/keys anything, the critter
//     retreats instantly. No animation — just gone.
//   - Per-world species: W1 fieldmouse, W2 fennec, W3 frog, etc.
//     Same Quiet Paper aesthetic — ink linework, no shading, no
//     saturated color.
//
// Why it fits the pillars (no streaks, no fishhooks, no nag):
//   - Silent (no sound)
//   - Passive (zero gameplay impact, can never block a slide)
//   - Legible (just visual flair; never claims to be relevant to the
//     puzzle state)
//   - No nag (retreats on input, never demands a tap, never holds
//     bonus content)
//
// It's company without obligation — exactly the Quiet Paper voice.
// Anti-mascot: never breaks the fourth wall, never speaks, never asks
// to be tapped.
//
// This component is rendered as a child of Board.tsx's SVG so the
// `transform={translate(x y)}` aligns with the same axial→pixel
// coordinate system the tiles use. It receives the list of empty
// cells from Board (Board already computes the layout).
import { useEffect, useRef, useState } from 'react';
import { axialToPixel, hexPath, TILE_SIZE } from '../hex-layout';
import type { AxialCoord } from '@tessera/engine';

const IDLE_THRESHOLD_MS = 45_000;
const PEEK_DURATION_MS = 6_000;
const COOLDOWN_MIN_MS = 30_000;
const COOLDOWN_RANGE_MS = 15_000; // 30-45s window per spec

interface Props {
  worldIndex: number;
  emptyCells: readonly AxialCoord[];
}

interface PeekState {
  cell: AxialCoord;
  id: number; // re-mount key so the CSS animation restarts cleanly
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduce(m.matches);
    update();
    m.addEventListener('change', update);
    return () => m.removeEventListener('change', update);
  }, []);
  return reduce;
}

export function HexCritter({ worldIndex, emptyCells }: Props) {
  const [peek, setPeek] = useState<PeekState | null>(null);
  const reduceMotion = usePrefersReducedMotion();
  // Stash the latest empty-cell list in a ref so the timer chain doesn't
  // need to be torn down on every board-state change. Without this, every
  // tile slide would reset the idle timer to 45s, defeating the purpose.
  const emptyRef = useRef(emptyCells);
  emptyRef.current = emptyCells;
  const peekRef = useRef<PeekState | null>(null);
  peekRef.current = peek;

  useEffect(() => {
    if (reduceMotion) return;

    let timer: number | null = null;

    const clear = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const fire = () => {
      const cells = emptyRef.current;
      if (cells.length === 0) {
        // No empty cells (densely-packed boards). Skip this peek window
        // and try again in a cooldown — by then the player may have
        // cleared some tiles.
        timer = window.setTimeout(fire, COOLDOWN_MIN_MS);
        return;
      }
      const cell = cells[Math.floor(Math.random() * cells.length)]!;
      const id = Date.now();
      setPeek({ cell, id });
      timer = window.setTimeout(() => {
        setPeek(null);
        // Schedule the next peek window. Random within 30-45s.
        const next = COOLDOWN_MIN_MS + Math.random() * COOLDOWN_RANGE_MS;
        timer = window.setTimeout(fire, next);
      }, PEEK_DURATION_MS);
    };

    const reset = () => {
      // User activity → retreat any visible critter and restart the
      // idle countdown. Retreat is instant; spec says "polite, no
      // scared animation."
      if (peekRef.current !== null) setPeek(null);
      clear();
      timer = window.setTimeout(fire, IDLE_THRESHOLD_MS);
    };

    // Listeners chosen to match "real input" — taps and presses, not hover.
    // pointermove was tempting but fires constantly during a passive cursor
    // hover, defeating the 45s idle window. The spec wants the critter to
    // retreat on player ACTIONS (tap/click/key), not on cursor proximity.
    document.addEventListener('touchstart', reset, { passive: true });
    document.addEventListener('mousedown', reset);
    document.addEventListener('keydown', reset);

    // Initial schedule.
    timer = window.setTimeout(fire, IDLE_THRESHOLD_MS);

    return () => {
      clear();
      document.removeEventListener('touchstart', reset);
      document.removeEventListener('mousedown', reset);
      document.removeEventListener('keydown', reset);
    };
  }, [reduceMotion]);

  if (!peek || reduceMotion) return null;

  const p = axialToPixel(peek.cell);

  return (
    <g
      key={peek.id}
      className="hex-critter"
      transform={`translate(${p.x} ${p.y})`}
      aria-hidden
    >
      {/* Clip-path keeps the critter's emerging head BELOW the hex's
          horizontal midline so it reads as "peeking from inside the
          cell" instead of floating over it. The clip is a rectangle
          covering the upper half of the hex bounding box. */}
      <defs>
        <clipPath id={`hexcrit-clip-${peek.id}`}>
          <path d={hexPath(TILE_SIZE)} />
        </clipPath>
      </defs>
      <g clipPath={`url(#hexcrit-clip-${peek.id})`}>
        <CritterHead worldIndex={worldIndex} />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Per-world critter heads. Each is a small ink-stroke "peek" — ears, eyes,
// optional snout. Rendered with currentColor so the theme inverts on dark
// mode like everything else.
//
// All heads are positioned with their TOP at y ≈ 0 so they emerge upward
// out of the hex's bottom half via the CSS animation in styles.css. The
// emerge motion is just a translateY from below the hex into view.
// ---------------------------------------------------------------------------
function CritterHead({ worldIndex }: { worldIndex: number }) {
  switch (worldIndex) {
    case 1: case 11: return <FieldMouseHead />;     // Threshold + Keep
    case 2: case 12: return <FennecHead />;         // Ember + Vault
    case 3: case 13: return <FrogHead />;           // Tide + Crucible
    case 4: case 14: return <ChipmunkHead />;       // Ashwood + Ballast
    case 5: case 15: return <ErmineHead />;         // Helix + Cairn
    case 6: return <PrismMothHead />;               // Prism (moth-with-curled-antenna)
    case 7: return <FawnHead />;                    // Aurora
    case 8: return <FoxKitHead />;                  // Quartz
    case 9: return <OwletHead />;                   // Cinder
    case 10: return <RavenHead />;                  // Obsidian
    default: return <FieldMouseHead />;
  }
}

// All heads share approximate dimensions: ~24px wide, ~16-22px tall,
// designed to emerge from a TILE_SIZE=34 hex with the bottom of the
// drawing at y=0 (so the head pokes UP out of the cell).

function FieldMouseHead() {
  // Round head with two distinct circular ears. Pink-tinted nose, dot eyes.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      {/* Ears — large round circles for mouse silhouette */}
      <circle cx={-7} cy={-15} r={4.5} fill="currentColor" />
      <circle cx={7} cy={-15} r={4.5} fill="currentColor" />
      <circle cx={-7} cy={-15} r={2.2} fill="var(--accent)" opacity="0.45" />
      <circle cx={7} cy={-15} r={2.2} fill="var(--accent)" opacity="0.45" />
      {/* Head — round dome */}
      <ellipse cx={0} cy={-9} rx={9} ry={7} fill="currentColor" />
      {/* Eye whites + pupils. The animation cycles eye direction. */}
      <Eyes baseY={-10} />
      {/* Nose — small accent dot at the tip */}
      <circle cx={0} cy={-5} r={1} fill="var(--accent)" opacity="0.9" />
      {/* Whiskers */}
      <g stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.55">
        <path d="M-2 -5 L-9 -3" />
        <path d="M2 -5 L9 -3" />
      </g>
    </g>
  );
}

function FennecHead() {
  // Big triangular ears (signature fennec feature), pointed snout.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      {/* Ears — tall triangles */}
      <path d="M-12 -6 L-9 -22 L-5 -10 Z" fill="currentColor" />
      <path d="M12 -6 L9 -22 L5 -10 Z" fill="currentColor" />
      <path d="M-10 -8 L-9 -18 L-7 -10 Z" fill="var(--accent)" opacity="0.4" />
      <path d="M10 -8 L9 -18 L7 -10 Z" fill="var(--accent)" opacity="0.4" />
      {/* Head — narrower than mouse */}
      <ellipse cx={0} cy={-9} rx={7.5} ry={6.5} fill="currentColor" />
      <Eyes baseY={-10} />
      {/* Pointed snout */}
      <path d="M-2.5 -5 L0 -2 L2.5 -5 Z" fill="currentColor" />
      <circle cx={0} cy={-3.5} r={0.9} fill="var(--accent)" opacity="0.9" />
    </g>
  );
}

function FrogHead() {
  // Wide low head with bulging eyes on top.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      {/* Bulging eye sockets — atop the head */}
      <circle cx={-6} cy={-13} r={4} fill="currentColor" />
      <circle cx={6} cy={-13} r={4} fill="currentColor" />
      <circle cx={-6} cy={-13} r={2.4} fill="var(--ivory)" />
      <circle cx={6} cy={-13} r={2.4} fill="var(--ivory)" />
      {/* Pupils — vertical slits, frog-like */}
      <ellipse cx={-6} cy={-13} rx={0.6} ry={2} fill="currentColor" />
      <ellipse cx={6} cy={-13} rx={0.6} ry={2} fill="currentColor" />
      {/* Wide low head */}
      <ellipse cx={0} cy={-7} rx={11} ry={6} fill="currentColor" />
      {/* Mouth line */}
      <path d="M-7 -5 Q0 -3 7 -5" stroke="var(--ivory)" strokeWidth="0.8" fill="none" opacity="0.5" />
    </g>
  );
}

function ChipmunkHead() {
  // Round head, slight cheek puff, prominent stripe on forehead.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      {/* Small rounded ears */}
      <ellipse cx={-7} cy={-13} rx={2.5} ry={3.5} fill="currentColor" />
      <ellipse cx={7} cy={-13} rx={2.5} ry={3.5} fill="currentColor" />
      {/* Head — round with subtle cheek puff */}
      <ellipse cx={0} cy={-8} rx={9.5} ry={7.5} fill="currentColor" />
      {/* Forehead stripe */}
      <path d="M-1 -14 L-1 -3 M1 -14 L1 -3" stroke="var(--ivory)" strokeWidth="0.6" opacity="0.4" fill="none" />
      <Eyes baseY={-10} />
      <circle cx={0} cy={-4.5} r={0.9} fill="var(--accent)" opacity="0.9" />
    </g>
  );
}

function ErmineHead() {
  // Slender head, small triangular ears, pointed snout.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      <path d="M-7 -10 L-5 -16 L-2 -11 Z" fill="currentColor" />
      <path d="M7 -10 L5 -16 L2 -11 Z" fill="currentColor" />
      <ellipse cx={0} cy={-9} rx={7} ry={6.5} fill="currentColor" />
      <Eyes baseY={-10} />
      <path d="M-2 -5 L0 -3 L2 -5 Z" fill="currentColor" />
      <circle cx={0} cy={-4} r={0.8} fill="var(--accent)" opacity="0.9" />
    </g>
  );
}

function PrismMothHead() {
  // Moth-styled with feathered antennae arching back.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      {/* Curled feathered antennae */}
      <path d="M-3 -10 Q-7 -16 -10 -18 Q-12 -19 -13 -20" stroke="currentColor" strokeWidth="0.9" fill="none" opacity="0.85" />
      <path d="M3 -10 Q7 -16 10 -18 Q12 -19 13 -20" stroke="currentColor" strokeWidth="0.9" fill="none" opacity="0.85" />
      {/* Comb-tooth feathering hint */}
      <g stroke="currentColor" strokeWidth="0.5" opacity="0.5">
        <path d="M-7 -16 L-9 -15" />
        <path d="M-9 -17 L-11 -16" />
        <path d="M7 -16 L9 -15" />
        <path d="M9 -17 L11 -16" />
      </g>
      {/* Round head */}
      <ellipse cx={0} cy={-8} rx={6.5} ry={6} fill="currentColor" />
      <Eyes baseY={-9} />
    </g>
  );
}

function FawnHead() {
  // Narrow elongated head, large soft ears, white spots hint.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      <ellipse cx={-8} cy={-13} rx={3} ry={5} fill="currentColor" transform="rotate(-15 -8 -13)" />
      <ellipse cx={8} cy={-13} rx={3} ry={5} fill="currentColor" transform="rotate(15 8 -13)" />
      <ellipse cx={0} cy={-8} rx={7.5} ry={7} fill="currentColor" />
      <Eyes baseY={-10} />
      <circle cx={0} cy={-4} r={1} fill="var(--accent)" opacity="0.9" />
      {/* Spots */}
      <g fill="var(--ivory)" opacity="0.35">
        <circle cx={-4} cy={-11} r={0.7} />
        <circle cx={4} cy={-11} r={0.7} />
      </g>
    </g>
  );
}

function FoxKitHead() {
  // Triangle ears, narrow muzzle, alert posture.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      <path d="M-9 -8 L-7 -17 L-3 -10 Z" fill="currentColor" />
      <path d="M9 -8 L7 -17 L3 -10 Z" fill="currentColor" />
      <ellipse cx={0} cy={-9} rx={8} ry={7} fill="currentColor" />
      <Eyes baseY={-10} />
      <path d="M-3 -5 L0 -2 L3 -5 Z" fill="currentColor" />
      <circle cx={0} cy={-3} r={1} fill="var(--accent)" opacity="0.9" />
      <path d="M-1 -14 L-1 -3" stroke="var(--ivory)" strokeWidth="0.5" opacity="0.3" fill="none" />
    </g>
  );
}

function OwletHead() {
  // Round head with a flat top, big circular eye discs.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      {/* Tufted ears */}
      <path d="M-8 -16 L-6 -10 L-4 -16 Z" fill="currentColor" />
      <path d="M8 -16 L6 -10 L4 -16 Z" fill="currentColor" />
      <ellipse cx={0} cy={-9} rx={9} ry={8} fill="currentColor" />
      {/* Eye discs — owl-signature oversized eyes */}
      <circle cx={-4} cy={-9} r={3.2} fill="var(--ivory)" />
      <circle cx={4} cy={-9} r={3.2} fill="var(--ivory)" />
      <circle cx={-4} cy={-9} r={1.8} fill="currentColor" />
      <circle cx={4} cy={-9} r={1.8} fill="currentColor" />
      {/* Beak */}
      <path d="M-1.5 -5 L0 -2 L1.5 -5 Z" fill="var(--accent)" opacity="0.9" />
    </g>
  );
}

function RavenHead() {
  // Sleek head, no ears, hint of beak, glossy oval.
  return (
    <g style={{ animation: 'hex-critter-emerge 6s ease-in-out forwards' }}>
      <ellipse cx={0} cy={-10} rx={8} ry={7.5} fill="currentColor" />
      {/* Eye — single visible side eye plus beak */}
      <circle cx={-1} cy={-11} r={1.4} fill="var(--ivory)" />
      <circle cx={-1} cy={-11} r={0.7} fill="currentColor" />
      <circle cx={3} cy={-11} r={1.4} fill="var(--ivory)" />
      <circle cx={3} cy={-11} r={0.7} fill="currentColor" />
      {/* Hooked beak */}
      <path d="M-3 -6 Q0 -1 3 -6 Q1 -8 -1 -8 Z" fill="currentColor" />
      <path d="M-1 -7 L1 -6" stroke="var(--ivory)" strokeWidth="0.4" opacity="0.5" />
      {/* Glossy highlight on head */}
      <ellipse cx={-2} cy={-13} rx={2} ry={1} fill="var(--ivory)" opacity="0.2" />
    </g>
  );
}

// Shared "two eyes that occasionally blink and glance" element.
// The animation is driven by CSS — eye-element class does the blink + dart.
function Eyes({ baseY }: { baseY: number }) {
  return (
    <g className="hex-critter-eyes">
      {/* Whites */}
      <circle cx={-3} cy={baseY} r={1.4} fill="var(--ivory)" />
      <circle cx={3} cy={baseY} r={1.4} fill="var(--ivory)" />
      {/* Pupils — these get translateX'd by the look-around animation. */}
      <g className="hex-critter-pupils">
        <circle cx={-3} cy={baseY} r={0.9} fill="currentColor" />
        <circle cx={3} cy={baseY} r={0.9} fill="currentColor" />
      </g>
    </g>
  );
}
